import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
interface ScannedProduct {
  title: string;
  editionLabel: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl: string;
}
const NAMES = ['María G.', 'Carlos R.', 'Ana P.', 'Diego M.', 'Sofía L.', 'Andrés T.', 'Valentina S.', 'Javier H.', 'Camila F.', 'Luis A.', 'Isabella N.', 'Fernando V.', 'Daniela C.', 'Roberto K.', 'Lucía B.', 'Miguel D.', 'Paula E.', 'Alejandro W.', 'Gabriela O.', 'Sebastián J.', 'Mariana Z.', 'Ricardo Q.', 'Natalia U.', 'Eduardo I.', 'Carolina Y.', 'Tomás X.', 'Andrea M.', 'Santiago P.', 'Valeria R.', 'Emilio S.', 'Laura T.', 'Martín H.', 'Claudia F.', 'Nicolás A.', 'Renata L.', 'Adrián B.'];
const CITIES = ['Miami', 'Los Angeles', 'Houston', 'New York', 'Chicago', 'San Antonio', 'Phoenix', 'Dallas', 'San Diego', 'Austin', 'Denver', 'Orlando', 'Las Vegas', 'San Francisco', 'Seattle', 'Portland', 'Atlanta', 'Tampa', 'Sacramento', 'Charlotte', 'Nashville', 'Albuquerque', 'Tucson', 'El Paso', 'San Jose', 'Fort Worth', 'Columbus', 'Indianapolis'];
const FALLBACK_PRODUCTS: ScannedProduct[] = [{
  title: 'Bundle Sculpt-It',
  editionLabel: 'Sistema Completo',
  priceNumber: 9.99,
  priceText: '$9.99 USD',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/ekb4a2HzQmsbpYKThDegia/mk-1.png'
}, {
  title: 'Bundle Sculpt-It + 50 Plantillas',
  editionLabel: 'Pack Arquitecto',
  priceNumber: 19.98,
  priceText: '$19.98 USD',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/r2KUtWvZggGoXSiNMeJwWC/order_bump-1.png'
}, {
  title: 'Bundle Sculpt-It + Studio Glow',
  editionLabel: 'Pack Profesional',
  priceNumber: 19.98,
  priceText: '$19.98 USD',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/wihCKhKsGoKPHZebrPbBBG/order_bumpt-2.png'
}, {
  title: 'Bundle Sculpt-It Completo',
  editionLabel: 'Pack Total',
  priceNumber: 29.97,
  priceText: '$29.97 USD',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/ekb4a2HzQmsbpYKThDegia/mk-1.png'
}];
function scanPageProducts(): ScannedProduct[] {
  const products: ScannedProduct[] = [];
  try {
    // Try JSON-LD first
    const jsonLdScripts = document.querySelectorAll('script[type="application/ld+json"]');
    jsonLdScripts.forEach(script => {
      try {
        const data = JSON.parse(script.textContent || '');
        if (data['@type'] === 'Product' && data.offers) {
          const offer = Array.isArray(data.offers) ? data.offers[0] : data.offers;
          products.push({
            title: data.name || 'Product',
            editionLabel: data.description?.substring(0, 30) || '',
            priceNumber: parseFloat(offer.price) || 0,
            priceText: `$${offer.price} ${offer.priceCurrency || 'USD'}`,
            thumbnailUrl: (Array.isArray(data.image) ? data.image[0] : data.image) || ''
          });
        }
      } catch {
        /* skip malformed JSON-LD */
      }
    });
    if (products.length > 0) return products;
    // Scan for price patterns in the DOM
    const priceRegex = /\$\s?\d+(\.\d{1,2})?/g;
    const allText = document.body.innerText || '';
    const priceMatches = allText.match(priceRegex);
    if (priceMatches && priceMatches.length > 0) {
      // Found prices but can't reliably pair with products
      // Return empty to use fallbacks
      return [];
    }
  } catch {
    // Scanner failed, use fallbacks
  }
  return products;
}
function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function getRandomInterval() {
  return Math.floor(Math.random() * 7000) + 8000; // 8-15 seconds
}
interface ToastData {
  id: number;
  name: string;
  city: string;
  product: ScannedProduct;
}
export function RecentPurchaseToast() {
  const [toast, setToast] = useState<ToastData | null>(null);
  const [visible, setVisible] = useState(false);
  const productsRef = useRef<ScannedProduct[]>([]);
  const nameHistoryRef = useRef<string[]>([]);
  const idCounterRef = useRef(0);
  const prefersReducedMotion = useRef(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    prefersReducedMotion.current = mq.matches;
    const handler = (e: MediaQueryListEvent) => {
      prefersReducedMotion.current = e.matches;
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  useEffect(() => {
    // Scan products on mount
    const scanned = scanPageProducts();
    productsRef.current = scanned.length > 0 ? scanned : FALLBACK_PRODUCTS;
  }, []);
  const pickName = useCallback(() => {
    const available = NAMES.filter(n => !nameHistoryRef.current.includes(n));
    const pool = available.length > 0 ? available : NAMES;
    const name = pickRandom(pool);
    nameHistoryRef.current.push(name);
    if (nameHistoryRef.current.length > 10) {
      nameHistoryRef.current.shift();
    }
    return name;
  }, []);
  const showToast = useCallback(() => {
    const product = pickRandom(productsRef.current);
    const name = pickName();
    const city = pickRandom(CITIES);
    const id = ++idCounterRef.current;
    setToast({
      id,
      name,
      city,
      product
    });
    setVisible(true);
    // Hide after 4 seconds
    setTimeout(() => {
      setVisible(false);
    }, 4000);
  }, [pickName]);
  useEffect(() => {
    // First toast after 3 seconds
    const initialTimeout = setTimeout(() => {
      showToast();
    }, 3000);
    return () => clearTimeout(initialTimeout);
  }, [showToast]);
  useEffect(() => {
    if (!visible && toast) {
      // Schedule next toast after current one exits
      const delay = getRandomInterval();
      const timeout = setTimeout(() => {
        showToast();
      }, delay);
      return () => clearTimeout(timeout);
    }
  }, [visible, toast, showToast]);
  const animationVariants = prefersReducedMotion.current ? {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 60
    },
    animate: {
      opacity: 1,
      y: 0
    },
    exit: {
      opacity: 0,
      y: 60
    }
  };
  return <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] pointer-events-none" data-id="element-272">
      <AnimatePresence mode="wait" data-id="element-273">
        {visible && toast && <motion.div key={toast.id} initial={animationVariants.initial} animate={animationVariants.animate} exit={animationVariants.exit} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} className="pointer-events-auto bg-surface border border-white/10 rounded-xl shadow-2xl shadow-black/40 px-4 py-3 flex items-center gap-3 max-w-sm sm:max-w-md" data-id="element-274">
            {/* Product thumbnail */}
            <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 border border-white/10" data-id="element-275">
              <img src={toast.product.thumbnailUrl} alt={toast.product.title} className="w-full h-full object-cover" data-id="element-276" />
            </div>

            {/* Content */}
            <div className="flex-grow min-w-0" data-id="element-277">
              <p className="text-sm text-text leading-snug" data-id="element-278">
                <strong className="text-accent" data-id="element-279">{toast.name}</strong>
                <span className="text-textMuted" data-id="element-280"> en {toast.city}</span>
              </p>
              <p className="text-xs text-textMuted mt-0.5 truncate" data-id="element-281">
                compró{' '}
                <span className="text-text font-medium" data-id="element-282">
                  {toast.product.editionLabel}
                </span>
                <span className="text-accent font-semibold ml-1" data-id="element-283">
                  — {toast.product.priceText}
                </span>
              </p>
            </div>

            {/* Live indicator */}
            <div className="flex-shrink-0 flex items-center gap-1" data-id="element-284">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" data-id="element-285"></span>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}